import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, from, map, Observable, of, tap } from 'rxjs';
import { SessionUser } from '@/common/interfaces/medusa.interface';
import { MEDUSA_CLIENT } from '@/common/services/medusa/medusa-client';
import { toSessionUser } from '@/common/services/medusa/medusa-customer.mapper';
import { isUnauthorizedMedusaError } from '@/common/services/medusa/medusa-error';

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly medusa = inject(MEDUSA_CLIENT);

  private readonly _status = signal<AuthStatus>('unknown');
  private readonly _user = signal<SessionUser | null>(null);
  private lastChecked = 0;
  private readonly ttlMs = 5 * 60 * 1000;
  /**
   * Orders session-state writes and refresh starts so a stale response can
   * never roll back a newer operation when refreshes overlap.
   */
  private writeSeq = 0;
  private appliedWriteSeq = 0;
  /**
   * Highest sequence of an actual state write (applied response, login,
   * logout, explicit rejection). Refresh *starts* do not advance it, so a
   * newer refresh merely starting cannot discard an explicit server
   * rejection from an older in-flight request.
   */
  private lastStateWriteSeq = 0;
  /**
   * Monotonic token that advances only when the underlying session is
   * replaced or cleared (login/account switch, logout, expiry, explicit
   * rejection) - never on an ordinary user-data refresh that keeps the same
   * session. Request handlers capture it at request start so a routine
   * refresh cannot invalidate the stale-session check of an in-flight
   * request, while a newer login/logout still discards older logout
   * decisions.
   */
  private sessionGenerationSeq = 0;

  status = this._status.asReadonly();
  user = this._user.asReadonly();

  /** Call once at app start or first need. Subsequent calls are cached unless stale. */
  ensureSession(forceRefresh = false): Observable<boolean> {
    if (!this.isBrowser) {
      return of(false);
    }

    const now = Date.now();
    const fresh = now - this.lastChecked < this.ttlMs;
    if (!forceRefresh && this._status() !== 'unknown' && fresh) {
      return of(this._status() === 'authenticated');
    }

    const seq = ++this.writeSeq;
    this.appliedWriteSeq = seq;
    let appliedResponse = false;

    return from(this.medusa.store.customer.retrieve()).pipe(
      map((response) =>
        response.customer ? toSessionUser(response.customer) : null,
      ),
      tap((user) => {
        // A newer write or refresh start landed while this fetch was in
        // flight; applying this snapshot would roll it back.
        if (seq < this.appliedWriteSeq) return;
        this.appliedWriteSeq = seq;
        const newStatus: AuthStatus = user
          ? 'authenticated'
          : 'unauthenticated';
        // Only a clear advances the session generation: a refresh that
        // keeps the same live session must leave it untouched so a genuine
        // 401 from an in-flight request stays authoritative instead of
        // being discarded as stale.
        if (
          newStatus === 'unauthenticated' &&
          this._status() !== 'unauthenticated'
        ) {
          this.sessionGenerationSeq += 1;
        }
        appliedResponse = true;
        this._user.set(user);
        this._status.set(newStatus);
        this.lastStateWriteSeq = seq;
        this.lastChecked = Date.now();
      }),
      map(() => appliedResponse && this._status() === 'authenticated'),
      catchError((error: unknown) => {
        this.lastChecked = Date.now();
        // Only an explicit session rejection clears client auth state; a
        // transient failure must not fake a logout.
        if (isUnauthorizedMedusaError(error)) {
          if (seq >= this.lastStateWriteSeq) {
            this.lastStateWriteSeq = seq;
            this.appliedWriteSeq = Math.max(this.appliedWriteSeq, seq);
            this._user.set(null);
            this._status.set('unauthenticated');
            this.sessionGenerationSeq += 1;
          }
          return of(false);
        }

        const keepSession = this._status() === 'authenticated';
        // Record this sequence even when the session is retained: the
        // operation still happened, so any older in-flight response stays
        // invalidated instead of applying a snapshot captured earlier.
        if (seq >= this.appliedWriteSeq) {
          this.appliedWriteSeq = seq;
          if (!keepSession) {
            this.lastStateWriteSeq = seq;
            this._user.set(null);
            this._status.set('unauthenticated');
            this.sessionGenerationSeq += 1;
          }
        }
        return of(false);
      }),
    );
  }

  /**
   * Records fresh user data for the current session. Deliberately does not
   * advance the session generation: callers also use this for ordinary
   * refreshes (profile/address updates), and a routine refresh must not
   * invalidate an in-flight request's stale-session check.
   */
  markAuthenticated(user: SessionUser | null | undefined) {
    this.appliedWriteSeq = ++this.writeSeq;
    this.lastStateWriteSeq = this.appliedWriteSeq;
    this._user.set(user ?? null);
    this._status.set('authenticated');
    this.lastChecked = Date.now();
  }

  markUnauthenticated() {
    this.appliedWriteSeq = ++this.writeSeq;
    this.lastStateWriteSeq = this.appliedWriteSeq;
    this._user.set(null);
    this._status.set('unauthenticated');
    this.sessionGenerationSeq += 1;
    this.lastChecked = Date.now();
  }

  /**
   * Invalidate any previously displayed identity without claiming to know the
   * new session yet - e.g. a successful login/registration that switches to a
   * different account.
   */
  clearUser(): void {
    this.appliedWriteSeq = ++this.writeSeq;
    this.lastStateWriteSeq = this.appliedWriteSeq;
    this._user.set(null);
    this._status.set('unknown');
    this.sessionGenerationSeq += 1;
    this.lastChecked = Date.now();
  }

  /**
   * Monotonic token for the current session. It advances only when the
   * underlying session is replaced or cleared.
   */
  sessionGeneration(): number {
    return this.sessionGenerationSeq;
  }

  hasPermission(...permissions: string[]): boolean {
    const currentPermissions = this.currentPermissions();
    return permissions.every((permission) =>
      currentPermissions.includes(permission),
    );
  }

  hasAnyPermission(...permissions: string[]): boolean {
    const currentPermissions = this.currentPermissions();
    return permissions.some((permission) =>
      currentPermissions.includes(permission),
    );
  }

  hasRole(..._roles: string[]): boolean {
    return false;
  }

  private currentPermissions(): string[] {
    // Medusa storefront customers do not carry admin roles or permissions.
    return [];
  }
}
